import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import Layout from '../components/layout/Layout'
import { Bot, Users, Save, ChevronLeft, Sparkles, Clock, CheckCircle, AlertCircle, FileText, Plus, Mic } from 'lucide-react'

const MEETING_TYPES = [
  { id: 'kickoff',     label: 'Reunión de inicio',                icon: '🚀' },
  { id: 'review',      label: 'Revisión de avance',               icon: '📊' },
  { id: 'workshop',    label: 'Taller de trabajo',                icon: '🛠️' },
  { id: 'delivery',    label: 'Entrega de resultados',            icon: '📦' },
  { id: 'strategic',   label: 'Sesión estratégica',               icon: '🎯' },
  { id: 'entrevista',  label: 'Entrevista de Modelo de Negocio',  icon: '🎤' },
]

const SECCIONES_ETAPA0 = [
  'Contexto y Entorno',
  'Propuesta de Valor',
  'Segmentos y Canales',
  'Fuentes de Ingreso y Estructura de Costos',
  'Capacidades y Recursos Clave',
  'Relaciones con Clientes',
  'Alianzas y Partners Estratégicos',
]

const GUIA_PREGUNTAS = {
  directivo: [
    '¿Cuál es su visión para la empresa en los próximos 3-5 años?',
    '¿Cuáles son los principales retos estratégicos que enfrenta hoy?',
    '¿Qué capacidades considera que son ventajas competitivas reales?',
    '¿Qué decisiones estratégicas están pendientes o bloqueadas?',
    '¿Cómo describiría la cultura organizacional actual?',
    '¿Qué resultados esperaría ver al finalizar este proceso de consultoría?',
  ],
  gerencia: [
    '¿Cuáles son los principales cuellos de botella en su área?',
    '¿Qué métricas utiliza para medir el desempeño de su equipo?',
    '¿Cómo se alinea su área con los objetivos corporativos?',
    '¿Qué recursos o capacidades le hacen falta para cumplir sus metas?',
    '¿Qué iniciativas de mejora están en marcha actualmente?',
    '¿Cómo es la coordinación con otras áreas?',
  ],
  operativo: [
    '¿Cuáles son los procesos más críticos de su día a día?',
    '¿Dónde ocurren los mayores reprocesos o desperdicios?',
    '¿Qué herramientas o sistemas utiliza en su trabajo?',
    '¿Qué dificultades encuentra al comunicar problemas hacia arriba?',
    '¿Hay prácticas informales que funcionan bien pero no están documentadas?',
  ],
  externo: [
    '¿Cuál es su percepción de la empresa desde afuera?',
    '¿Cómo describiría la propuesta de valor de la empresa?',
    '¿Qué diferencia a esta empresa de sus competidores?',
    '¿Qué mejoras esperaría como cliente/aliado/proveedor?',
  ],
  otro: [
    '¿Cuál es su relación con la organización?',
    '¿Qué perspectiva única puede aportar al proceso?',
    '¿Qué oportunidades o riesgos percibe desde su posición?',
  ],
}

export default function ReunionPage() {
  const { profile } = useAuth()
  const navigate = useNavigate()

  // Step 1: select project + meeting type
  const [step, setStep] = useState('select') // 'select' | 'meeting'
  const [consultancies, setConsultancies] = useState([])
  const [selectedConsultancy, setSelectedConsultancy] = useState(null)
  const [meetingType, setMeetingType] = useState('review')
  const [attendees, setAttendees] = useState('')
  const [loadingProject, setLoadingProject] = useState(false)

  // Step 2: meeting
  const [projectData, setProjectData] = useState(null)
  const [summary, setSummary] = useState('')
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [notes, setNotes] = useState('')
  const [decisions, setDecisions] = useState('')
  const [nextSteps, setNextSteps] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Entrevista mode
  const [stakeholders, setStakeholders] = useState([])
  const [selectedStakeholder, setSelectedStakeholder] = useState(null)
  const [afirmacionesPendientes, setAfirmacionesPendientes] = useState([])  // [{texto, seccion, fuente}]
  const [newAfirmacion, setNewAfirmacion] = useState({ texto: '', seccion: SECCIONES_ETAPA0[0] })
  const [savedReunionId, setSavedReunionId] = useState(null)

  useEffect(() => { loadConsultancies() }, [])

  async function loadStakeholders(consultancyId) {
    const { data } = await supabase
      .from('stakeholders')
      .select('*')
      .eq('consultancy_id', consultancyId)
      .order('prioridad')
    setStakeholders(data || [])
  }

  async function loadConsultancies() {
    const { data } = await supabase
      .from('consultancies')
      .select('id, name, client_company, status')
      .order('created_at', { ascending: false })
    setConsultancies(data || [])
  }

  async function startMeeting() {
    if (!selectedConsultancy) return
    setLoadingProject(true)

    const [phasesRes, busRes, reportsRes] = await Promise.all([
      supabase.from('phases')
        .select('id, name, status, progress, phase_order, business_unit_id, sections(id, name, status, tasks(id, name, status))')
        .eq('consultancy_id', selectedConsultancy.id)
        .order('phase_order'),
      supabase.from('business_units')
        .select('id, name')
        .eq('consultancy_id', selectedConsultancy.id),
      supabase.from('ai_reports')
        .select('id, report_type, created_at')
        .eq('consultancy_id', selectedConsultancy.id)
        .order('created_at', { ascending: false })
        .limit(10),
    ])

    const pd = {
      consultancy: selectedConsultancy,
      phases: phasesRes.data || [],
      bus: busRes.data || [],
      reports: reportsRes.data || [],
    }
    setProjectData(pd)
    setLoadingProject(false)
    setStep('meeting')

    // Load stakeholders for entrevista mode
    if (meetingType === 'entrevista') {
      loadStakeholders(selectedConsultancy.id)
    } else {
      // Auto-generate AI summary for other meeting types
      generateSummary(pd)
    }
  }

  function buildProjectContext(pd) {
    const { consultancy, phases, bus, reports } = pd
    let ctx = `Empresa: ${consultancy.name} — ${consultancy.client_company || ''}\n`
    ctx += `Estado: ${consultancy.status}\n\n`

    const projectPhases = phases.filter(p => !p.business_unit_id)
    const buPhases = phases.filter(p => p.business_unit_id)

    if (projectPhases.length > 0) {
      ctx += 'FASES CORPORATIVAS:\n'
      for (const p of projectPhases) {
        const secs = p.sections || []
        const done = secs.reduce((a, s) => a + (s.tasks?.filter(t => t.status === 'completed').length || 0), 0)
        const total = secs.reduce((a, s) => a + (s.tasks?.length || 0), 0)
        ctx += `• ${p.name}: ${p.progress ?? 0}% (${done}/${total} tareas) — ${p.status}\n`
      }
    }

    if (bus.length > 0) {
      ctx += '\nUNIDADES DE NEGOCIO:\n'
      for (const bu of bus) {
        const bPhases = buPhases.filter(p => p.business_unit_id === bu.id)
        const avgProg = bPhases.length
          ? Math.round(bPhases.reduce((a, p) => a + (p.progress ?? 0), 0) / bPhases.length)
          : 0
        ctx += `• ${bu.name}: ${avgProg}% avance global\n`
        for (const p of bPhases) {
          ctx += `  - ${p.name}: ${p.progress ?? 0}% — ${p.status}\n`
        }
      }
    }

    ctx += `\nInformes generados: ${reports.length}\n`
    return ctx
  }

  async function generateSummary(pd) {
    setLoadingSummary(true)
    const meetingLabel = MEETING_TYPES.find(m => m.id === meetingType)?.label || 'Reunión'
    const context = buildProjectContext(pd)

    const prompt = `Eres el Agente Estratégico de Next Valley Consultants. Genera un resumen de avance para la reunión de tipo "${meetingLabel}" del proyecto ${pd.consultancy.name}.

ESTADO ACTUAL DEL PROYECTO:
${context}

Genera un resumen ejecutivo para comenzar la reunión que incluya:
1. **Situación actual** (2-3 líneas): dónde está el proyecto hoy
2. **Avances desde la última reunión**: qué se completó
3. **En progreso**: qué está actualmente en trabajo
4. **Pendiente / bloqueado**: qué necesita atención
5. **Agenda sugerida** para esta reunión de ${meetingLabel}

Sé específico, usa los datos del proyecto. Tono: profesional y directo.`

    try {
      const { data, error } = await supabase.functions.invoke('ai-report', {
        body: {
          context: prompt,
          reportType: 'summary',
        }
      })
      if (error) throw error
      setSummary(data?.report || '')
    } catch (e) {
      setSummary(`❌ Error generando resumen: ${e.message}`)
    }
    setLoadingSummary(false)
  }

  async function saveMeetingNotes() {
    if (!projectData) return
    setSaving(true)

    const meetingLabel = MEETING_TYPES.find(m => m.id === meetingType)?.label || 'Reunión'
    const isEntrevista = meetingType === 'entrevista'

    const content = isEntrevista
      ? [
          `# Entrevista de Modelo de Negocio — ${projectData.consultancy.name}`,
          `Fecha: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`,
          selectedStakeholder ? `Entrevistado: ${selectedStakeholder.nombre}${selectedStakeholder.cargo ? ` (${selectedStakeholder.cargo})` : ''}` : '',
          attendees ? `Participantes NVC: ${attendees}` : '',
          '',
          notes ? `## Notas de la entrevista\n${notes}` : '',
          afirmacionesPendientes.length > 0
            ? `## Afirmaciones capturadas (${afirmacionesPendientes.length})\n` +
              afirmacionesPendientes.map(a => `- [${a.seccion}] ${a.texto}`).join('\n')
            : '',
        ].filter(Boolean).join('\n\n')
      : [
          `# ${meetingLabel} — ${projectData.consultancy.name}`,
          `Fecha: ${new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`,
          attendees ? `Asistentes: ${attendees}` : '',
          '',
          '## Resumen de Avance (Agente IA)',
          summary,
          '',
          notes ? `## Notas de la reunión\n${notes}` : '',
          decisions ? `## Decisiones tomadas\n${decisions}` : '',
          nextSteps ? `## Próximos pasos\n${nextSteps}` : '',
        ].filter(Boolean).join('\n\n')

    try {
      const { data: reportData } = await supabase.from('ai_reports').insert({
        consultancy_id: projectData.consultancy.id,
        entity_type: 'consultancy',
        entity_id: projectData.consultancy.id,
        report_type: 'meeting_notes',
        content,
        generated_by: profile?.id,
      }).select().single()

      const reunionId = reportData?.id

      // Entrevista extras: save afirmaciones + update stakeholder
      if (isEntrevista && reunionId) {
        setSavedReunionId(reunionId)

        // Save afirmaciones
        if (afirmacionesPendientes.length > 0) {
          await supabase.from('afirmaciones').insert(
            afirmacionesPendientes.map(a => ({
              consultancy_id: projectData.consultancy.id,
              seccion_nombre: a.seccion,
              texto: a.texto,
              fuente: selectedStakeholder?.nombre || attendees || '',
              stakeholder_id: selectedStakeholder?.id || null,
              reunion_id: reunionId,
              estado: 'Sin validar',
              prioridad: 'media',
            }))
          )
        }

        // Update stakeholder estado_entrevista
        if (selectedStakeholder) {
          await supabase.from('stakeholders').update({
            estado_entrevista: 'Realizada',
            reunion_id: reunionId,
            fecha_entrevista: new Date().toISOString().split('T')[0],
            updated_at: new Date().toISOString(),
          }).eq('id', selectedStakeholder.id)
        }
      }

      setSaved(true)
    } catch (e) {
      alert('Error al guardar: ' + e.message)
    }
    setSaving(false)
  }

  function addAfirmacionPendiente() {
    if (!newAfirmacion.texto.trim()) return
    setAfirmacionesPendientes(prev => [...prev, { ...newAfirmacion }])
    setNewAfirmacion(prev => ({ ...prev, texto: '' }))
  }

  function removeAfirmacion(idx) {
    setAfirmacionesPendientes(prev => prev.filter((_, i) => i !== idx))
  }

  function downloadNotes() {
    const meetingLabel = MEETING_TYPES.find(m => m.id === meetingType)?.label || 'Reunion'
    const content = [
      `REUNIÓN: ${meetingLabel}`,
      `Proyecto: ${projectData?.consultancy?.name}`,
      `Fecha: ${new Date().toLocaleDateString('es-CO')}`,
      attendees ? `Asistentes: ${attendees}` : '',
      '',
      '─── RESUMEN AGENTE IA ───',
      summary,
      '',
      notes ? `─── NOTAS ───\n${notes}` : '',
      decisions ? `─── DECISIONES ───\n${decisions}` : '',
      nextSteps ? `─── PRÓXIMOS PASOS ───\n${nextSteps}` : '',
    ].filter(Boolean).join('\n\n')

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `reunion-${(projectData?.consultancy?.name || 'nvc').toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.txt`
    a.click()
  }

  // ── Screen: project selector ──────────────────────────────────────────────
  if (step === 'select') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Modo Reunión</h1>
          <p className="text-gray-500 text-sm mt-1">Genera un resumen automático de avance y toma notas de la reunión</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Project selector */}
          <div className="card space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} className="text-brand-600" />
              <h2 className="font-semibold text-gray-900">Selecciona un proyecto</h2>
            </div>
            {consultancies.length === 0 ? (
              <p className="text-sm text-gray-400">No hay consultorías disponibles.</p>
            ) : (
              <div className="space-y-2">
                {consultancies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedConsultancy(c)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedConsultancy?.id === c.id
                        ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-400'
                        : 'border-gray-200 hover:border-brand-300 hover:bg-brand-50/50'
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">{c.name}</div>
                    <div className="text-xs text-gray-500">{c.client_company || 'Sin empresa'}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Meeting options */}
          <div className="card space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={18} className="text-brand-600" />
              <h2 className="font-semibold text-gray-900">Tipo de reunión</h2>
            </div>
            <div className="space-y-2">
              {MEETING_TYPES.map(m => (
                <button
                  key={m.id}
                  onClick={() => setMeetingType(m.id)}
                  className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    meetingType === m.id
                      ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-400'
                      : 'border-gray-200 hover:border-brand-300 hover:bg-brand-50/50'
                  }`}
                >
                  <span className="text-xl">{m.icon}</span>
                  <span className="font-medium text-sm text-gray-900">{m.label}</span>
                </button>
              ))}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Asistentes (opcional)</label>
              <input
                type="text"
                value={attendees}
                onChange={e => setAttendees(e.target.value)}
                placeholder="Juan, María, Carlos..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>
        </div>

        <button
          onClick={startMeeting}
          disabled={!selectedConsultancy || loadingProject}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          {loadingProject ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Cargando proyecto...</>
          ) : (
            <><Sparkles size={16} /> Iniciar reunión</>
          )}
        </button>
      </div>
    )
  }

  // ── Screen: meeting mode ───────────────────────────────────────────────────
  const meetingLabel = MEETING_TYPES.find(m => m.id === meetingType)?.label || 'Reunión'
  const meetingIcon = MEETING_TYPES.find(m => m.id === meetingType)?.icon || '📋'
  const isEntrevistaMode = meetingType === 'entrevista'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => {
            setStep('select'); setSummary(''); setNotes(''); setDecisions(''); setNextSteps(''); setSaved(false)
            setSelectedStakeholder(null); setAfirmacionesPendientes([]); setNewAfirmacion({ texto: '', seccion: SECCIONES_ETAPA0[0] })
          }}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mt-1"
        >
          <ChevronLeft size={16} /> Volver
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{meetingIcon}</span>
            <h1 className="text-2xl font-bold text-gray-900">{meetingLabel}</h1>
          </div>
          <p className="text-gray-500 text-sm">{projectData?.consultancy?.name} · {projectData?.consultancy?.client_company}</p>
          {attendees && <p className="text-xs text-gray-400 mt-0.5">Asistentes: {attendees}</p>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadNotes}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-50 transition-all"
          >
            <FileText size={15} /> Descargar
          </button>
          <button
            onClick={saveMeetingNotes}
            disabled={saving || saved}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
              saved
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-brand-700 text-white hover:bg-brand-600'
            } disabled:opacity-70`}
          >
            {saving ? (
              <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Guardando...</>
            ) : saved ? (
              <><CheckCircle size={15} /> Guardado en la app</>
            ) : (
              <><Save size={15} /> Guardar en la app</>
            )}
          </button>
        </div>
      </div>

      {/* ── ENTREVISTA MODE ── */}
      {isEntrevistaMode ? (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left panel: guide */}
          <div className="lg:col-span-3 space-y-4">
            {/* Stakeholder selector */}
            <div className="card">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">🎤</span>
                <div className="font-semibold text-gray-900 text-sm">Entrevistado</div>
              </div>
              {stakeholders.length === 0 ? (
                <p className="text-sm text-gray-400">No hay stakeholders registrados. Agrégalos en la pestaña Stakeholders de Etapa 0.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {stakeholders.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedStakeholder(s)}
                      className={`text-left p-3 rounded-lg border transition-all ${
                        selectedStakeholder?.id === s.id
                          ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-400'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900">{s.nombre}</div>
                      <div className="text-xs text-gray-500">{s.cargo || s.tipo_rol}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Interview guide */}
            {selectedStakeholder && (
              <div className="card border-purple-100 bg-purple-50/30">
                <div className="font-semibold text-sm text-purple-900 mb-3 flex items-center gap-2">
                  <Mic size={15} className="text-purple-600" />
                  Guía de preguntas — {selectedStakeholder.nombre}
                </div>
                <ol className="space-y-2 pl-4 list-decimal">
                  {(GUIA_PREGUNTAS[selectedStakeholder.tipo_rol] || GUIA_PREGUNTAS.otro).map((q, i) => (
                    <li key={i} className="text-sm text-gray-700 leading-relaxed">{q}</li>
                  ))}
                </ol>
                <div className="mt-4 p-3 bg-purple-100 rounded-lg text-xs text-purple-700">
                  💡 Adapta las preguntas según el contexto. Escucha activamente y captura las ideas clave como afirmaciones.
                </div>
              </div>
            )}
          </div>

          {/* Right panel: notes + afirmaciones */}
          <div className="lg:col-span-2 space-y-4">
            <div className="card space-y-4">
              <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                📝 Notas de la entrevista
              </h3>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Apuntes libres durante la entrevista..."
                rows={5}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500 resize-y"
              />
            </div>

            {/* Afirmaciones capture */}
            <div className="card space-y-3">
              <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                💬 Afirmaciones capturadas
                <span className="ml-auto text-xs font-normal text-gray-400">{afirmacionesPendientes.length} afirmación{afirmacionesPendientes.length !== 1 ? 'es' : ''}</span>
              </div>

              {/* Add afirmacion */}
              <div className="space-y-2">
                <textarea
                  value={newAfirmacion.texto}
                  onChange={e => setNewAfirmacion(a => ({ ...a, texto: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) addAfirmacionPendiente() }}
                  placeholder="Escribe una idea, hallazgo u observación clave… (Ctrl+Enter para agregar)"
                  rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
                <div className="flex gap-2">
                  <select
                    value={newAfirmacion.seccion}
                    onChange={e => setNewAfirmacion(a => ({ ...a, seccion: e.target.value }))}
                    className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-purple-400"
                  >
                    {SECCIONES_ETAPA0.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button
                    onClick={addAfirmacionPendiente}
                    disabled={!newAfirmacion.texto.trim()}
                    className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-xs font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-colors"
                  >
                    <Plus size={12} /> Agregar
                  </button>
                </div>
              </div>

              {/* List */}
              {afirmacionesPendientes.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {afirmacionesPendientes.map((a, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex-1">
                        <div className="text-xs text-gray-700 leading-relaxed">{a.texto}</div>
                        <div className="text-xs text-yellow-600 mt-0.5 font-medium">{a.seccion}</div>
                      </div>
                      <button onClick={() => removeAfirmacion(idx)} className="text-gray-300 hover:text-red-400 text-sm flex-shrink-0">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {saved && (
              <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold">Entrevista guardada</div>
                  <div className="text-xs mt-0.5">
                    {afirmacionesPendientes.length > 0 && `${afirmacionesPendientes.length} afirmaciones guardadas. `}
                    {selectedStakeholder && 'Stakeholder marcado como Realizada.'}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (

      <div className="grid lg:grid-cols-5 gap-6">
        {/* AI Summary */}
        <div className="lg:col-span-3 space-y-4">
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Bot size={15} className="text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">Resumen de Avance — Agente IA</div>
                <div className="text-xs text-gray-400">Generado automáticamente al inicio de la reunión</div>
              </div>
              <button
                onClick={() => generateSummary(projectData)}
                disabled={loadingSummary}
                className="ml-auto text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
              >
                ↺ Regenerar
              </button>
            </div>

            {loadingSummary ? (
              <div className="flex items-center gap-3 py-6 text-gray-400 text-sm">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                Generando resumen del proyecto...
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto">
                {summary || 'No se pudo generar el resumen.'}
              </div>
            )}
          </div>

          {/* Project status quick view */}
          {projectData?.phases?.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Estado rápido del proyecto</h3>
              <div className="space-y-2">
                {projectData.phases.map(phase => {
                  const secs = phase.sections || []
                  const done = secs.reduce((a, s) => a + (s.tasks?.filter(t => t.status === 'completed').length || 0), 0)
                  const total = secs.reduce((a, s) => a + (s.tasks?.length || 0), 0)
                  const pct = total > 0 ? Math.round((done / total) * 100) : (phase.progress ?? 0)
                  return (
                    <div key={phase.id} className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-blue-400' : 'bg-gray-300'
                      }`} />
                      <span className="text-xs text-gray-600 flex-1 truncate">{phase.name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Notes section */}
        <div className="lg:col-span-2 space-y-4">
          <div className="card space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <FileText size={15} className="text-brand-500" /> Notas de la reunión
            </h3>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">📝 Notas generales</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Temas discutidos, contexto importante, observaciones..."
                rows={5}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-y"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">✅ Decisiones tomadas</label>
              <textarea
                value={decisions}
                onChange={e => setDecisions(e.target.value)}
                placeholder="Acuerdos y decisiones clave de esta reunión..."
                rows={4}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-y"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">🚀 Próximos pasos</label>
              <textarea
                value={nextSteps}
                onChange={e => setNextSteps(e.target.value)}
                placeholder="Responsables, fechas, acciones concretas para la próxima reunión..."
                rows={4}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brand-500 resize-y"
              />
            </div>
          </div>

          {saved && (
            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
              <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>Notas guardadas correctamente. Puedes verlas en el Portal del Cliente en la pestaña "Informes IA".</span>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  )
}
